import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BlogPost } from "@/hooks/useBlogPosts";
import OptimizedImage from "@/components/OptimizedImage";

interface BlogCardProps {
  post: BlogPost;
  featured?: boolean;
}

const BlogCard = ({ post, featured = false }: BlogCardProps) => {
  const publishedDate = post.published_at 
    ? format(new Date(post.published_at), "MMM d, yyyy")
    : format(new Date(post.created_at), "MMM d, yyyy");

  return (
    <Card className={`group overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${featured ? 'md:col-span-2 md:grid md:grid-cols-2' : ''}`}>
      <Link to={`/blog/${post.slug}`} className="block overflow-hidden" aria-label={post.title}>
        {post.featured_image ? (
          <div className={`overflow-hidden ${featured ? 'h-full min-h-[250px]' : ''}`}>
            <OptimizedImage
              src={post.featured_image}
              alt={post.title}
              priority={featured}
              aspectRatio={featured ? "auto" : "video"}
              className="transition-transform duration-500 group-hover:scale-105"
              wrapperClassName={featured ? "h-full min-h-[250px]" : ""}
            />
          </div>
        ) : (
          <div className={`bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center ${featured ? 'h-full min-h-[250px]' : 'aspect-video'}`}>
            <span className="text-4xl font-bold text-primary/30">
              {post.title.charAt(0)}
            </span>
          </div>
        )}
      </Link>
      
      <div className="flex flex-col">
        <CardHeader className="pb-2">
          {post.category && (
            <Link to={`/blog/category/${post.category.slug}`}>
              <Badge variant="secondary" className="w-fit hover:bg-primary hover:text-primary-foreground transition-colors">
                {post.category.name}
              </Badge>
            </Link>
          )}
          <Link to={`/blog/${post.slug}`}>
            <h3 className={`font-bold leading-tight hover:text-primary transition-colors ${featured ? 'text-2xl' : 'text-lg'}`}>
              {post.title}
            </h3>
          </Link>
        </CardHeader>
        
        <CardContent className="flex-1">
          {post.excerpt && (
            <p className="text-muted-foreground line-clamp-2">
              {post.excerpt}
            </p>
          )}
        </CardContent>
        
        <CardFooter className="pt-0 flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {publishedDate}
            </span>
            {post.reading_time && (
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {post.reading_time} min read
              </span>
            )}
          </div>
          <Link 
            to={`/blog/${post.slug}`}
            className="flex items-center gap-1 text-primary hover:gap-2 transition-all"
          >
            Read more<span className="sr-only"> about {post.title}</span>
            <ArrowRight className="h-4 w-4" />
          </Link>
        </CardFooter>
      </div>
    </Card>
  );
};

export default BlogCard;
